import { useFormikContext } from 'formik';
import type { FC } from 'react';
import React from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { Floor } from '../../models/Floor';
import { MapRelay } from '../../models/MapRelay';
import { WizardSummaryLayout } from '../wizard/wizard-summary-layout';

interface SummaryStepProps {
    onBack: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const SummaryStep: FC<SummaryStepProps> = ({ onBack }) => {
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const mappedFloors: Floor[] = values.unmappedFloors ? values.floors.filter((floor: Floor) => !values.unmappedFloors.includes(floor.level)) : values.floors;

    return <WizardSummaryLayout onBack={onBack} submitLabel={translate.byKey(TranslationKeys.confirm)}>
        <div className='flex flex-col w-full overflow-y-auto'>
            <div className='grid grid-cols-5 gap-2'>
                {/* Start Details */}
                <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.details)}</span>
                <div className='flex flex-col col-start-2 col-end-4'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.site)}</span>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.name)}</span>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.building)}</span>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.startingFloor)}</span>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.lastFloor)}</span>
                </div>
                <div className='flex flex-col col-start-4 col-end-6'>
                    <span className='text-xs'>{values.siteName}</span>
                    <span className='text-xs'>{values.name}</span>
                    <span className='text-xs'>{values.buildingName}</span>
                    <span className='text-xs'>{values.startingFloor}</span>
                    <span className='text-xs'>{values.lastFloor}</span>
                </div>
                {/* End Details */}
                <hr className='col-start-1 col-end-6' />
                {/* Start Floors */}
                <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.floors)}</span>
                <div className='flex flex-col col-start-2 col-end-4'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.name)}</span>
                    {mappedFloors.map((floor: Floor, index: number) => (
                        <span key={index} className='text-xs'>{floor.name}</span>
                    ))}
                </div>
                <div className='flex flex-col col-start-4 col-end-6'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.level)}</span>
                    {mappedFloors.map((floor: Floor, index: number) => (
                        <span key={index} className='text-xs'>{floor.level}</span>
                    ))}
                </div>
                {/* End Floors */}
                <hr className='col-start-1 col-end-6' />
                {/* Start Reader */}
                <>
                    <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.reader)}</span>

                    <div className='flex flex-col col-start-2 col-end-4'>
                        <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.name)}</span>
                        <span className='text-xs'>{values.readerName}</span>
                    </div>
                    <div className='flex flex-col col-start-4 col-end-6'>
                        <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.fixedAddress)}</span>
                        <span className='text-xs'>{values.readerFixedAddress}</span>
                    </div>
                    <hr className='col-start-1 col-end-6' />
                </>
                {/* End Reader */}
                {/* Start IO Devices */}
                <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.ioModules)}</span>
                <div className='flex flex-col col-start-2 col-end-4'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.name)}</span>
                    {values.mapRelays.map((ioModule: MapRelay, index: number) => (
                        <span key={index} className='text-xs'>{ioModule.name}</span>
                    ))}
                </div>
                <div className='flex flex-col col-start-4 col-end-6'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.fixedAddress)}</span>
                    {values.mapRelays.map((ioModule: MapRelay, index: number) => (
                        <span key={index} className='text-xs'>{ioModule.fixedAddress}</span>
                    ))}
                </div>
                {/* End IO Devices */}
                <hr className='col-start-1 col-end-6' />
                {/* Start Relay Mappings */}
                <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.mapRelays)}</span>
                <div className='flex flex-col'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.name)}</span>
                    {values.mapRelays.map((mapRelay: MapRelay, index: number) => (
                        <span key={index} className='text-xs'>{mapRelay.name}</span>
                    ))}
                </div>
                <div className='flex flex-col'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.floor)}</span>
                    {values.mapRelays.map((mapRelay: MapRelay, index: number) => (
                        <span key={index} className='text-xs'>{mapRelay.floors}</span>
                    ))}
                </div>
                <div className='flex flex-col'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.relays)}</span>
                    {values.mapRelays.map((mapRelay: MapRelay, index: number) => (
                        <span key={index} className='text-xs'>{mapRelay.relays}</span>
                    ))}
                </div>
                <div className='flex flex-col'>
                    <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.inputs)}</span>
                    {values.mapRelays.map((mapRelay: MapRelay, index: number) => (
                        <span key={index} className='text-xs'>{mapRelay.inputs}</span>
                    ))}
                </div>
                {/* End Relay Mappings */}
                {/* Start Permissions */}
                {/* <hr className='col-start-1 col-end-6' />
                <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.permissions)}</span>
                <div className='flex flex-col col-start-2 col-end-6 space-y-2'>
                    <div className='flex flex-row space-x-2'>
                        <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.createFloorGroupsPerFloor)}</span>
                        <span className='text-xs'>{values.autoCreateFloorGroup ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no)}</span>
                    </div>
                    <div className='flex flex-row space-x-2'>
                        <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.createFullAccessFloorGroup)}</span>
                        <span className='text-xs'>{values.autoCreateFullAccessFloorGroup ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no)}</span>
                    </div>
                    <div className='flex flex-row space-x-2'>
                        <span className='text-xs font-semibold'>{translate.byKey(TranslationKeys.createFullAccessProfile)}</span>
                        <span className='text-xs'>{values.autoCreateAccessProfile ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no)}</span>
                    </div>
                </div> */}
                {/* End Permission */}
                    {/* Start Floor Group */}
                {(values.autoCreateFullAccessFloorGroup || values.autoCreateFloorGroup) && <>
                    <hr className='col-start-1 col-end-6' />
                    <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.floorGroups)}</span>
                    <div className='flex flex-col col-start-2 col-end-6 space-y-2'>
                        {values.autoCreateFullAccessFloorGroup && <span className='text-xs col-start-2 col-end-6'>{translate.byKeyFormatted(TranslationKeys.floorGroupName, [values.name])}</span>}
                        {values.autoCreateFloorGroup && <span className='text-xs col-start-2 col-end-6'>{translate.byKey(TranslationKeys.createFloorGroupsPerFloor)}</span>}
                    </div>
                </>}
                    {/* End Floor Group */}
                {/* Start Access Profile */}
                {(values.autoCreateAccessProfile) && <>
                    <hr className='col-start-1 col-end-6' />
                    <span className='text-xs text-gray-500'>{translate.byKey(TranslationKeys.accessProfiles)}</span>
                    <div className='flex flex-col col-start-2 col-end-6 space-y-2'>
                        {values.autoCreateAccessProfile && <span className='text-xs col-start-2 col-end-6'>{translate.byKeyFormatted(TranslationKeys.accessProfileName, [values.name])}</span>}
                    </div>
                </>}
                { /* End Access Profile */ }
            </div>
        </div>
    </WizardSummaryLayout>
}

export default SummaryStep;